import Cropper from "cropperjs";
import React, { useContext, useState } from "react";
import { isVideoFile } from "../../../helpers/Utils";
import { BuyGiftCardContext } from "../../../pages/BuyGiftCardPageNew";
import DataService from "../../../services/DataService.service";
import { AppSettingContext } from "../../Application";
import LoadingComponent from "../../shared/LoadingComponent";

interface IMediaUploaderProps {
    uploaderId: number;
    selectedMediaUrl: string;
    lastUploadedTemplate: string;
    isVideoAllowed: boolean;
    imageCropperAspectRatio: number;

    setLastUploadedTemplate: (url: string) => void;
    handleMediaChange: (mediaUrl: string) => void;
}

const MediaUploader = (props: IMediaUploaderProps) => {
    const { id } = useContext(BuyGiftCardContext);
    const { labels, cardSettings } = useContext(AppSettingContext);
    const culture = document.documentElement.lang;

    //#region STATE PROPERTIES

    const [isMediaLoading, setIsMediaLoading] = useState<boolean>();
    const [dragging, setDragging] = useState(false);
    const [isMediaDropping, setIsMediaDropping] = useState(false);
    const [fileUploadError, setFileUploadError] = useState("");

    //#endregion

    //#region HANDLERS

    const handleDragOver = (event: React.DragEvent<HTMLLabelElement>): void => {
        event.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = (): void => {
        setDragging(false);
    };

    const handleDrop = async (event: React.DragEvent<HTMLLabelElement>): Promise<void> => {
        event.preventDefault();
        setDragging(false);

        const file = event.dataTransfer.files[0];
        if (!isMediaFileValid(file)) {
            return;
        }

        setIsMediaDropping(true);
        setTimeout(async () => {
            if (props.isVideoAllowed && isVideoFile(file.name)) {
                await processVideoFile(file);
            } else {
                initializeImageCropper(file);
            }

            setIsMediaDropping(false);
        }, 300);
    };

    const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = e.target.files[0];
        if (!isMediaFileValid(file)) {
            return;
        }

        if (props.isVideoAllowed && isVideoFile(file.name)) {
            await processVideoFile(file);
        } else {
            initializeImageCropper(file);
        }
    };

    //#endregion

    //#region COMMON HELPERS

    const isMediaFileValid = (file: File): boolean => {
        if (!file) {
            return false;
        }

        if (!file.type.startsWith("image/") && (!file.type.startsWith("video/") || !props.isVideoAllowed)) {
            console.error(`File is not of an image${props.isVideoAllowed ? "/video" : ""} type. File type: ${file.type}`);
            setFileUploadError(`File is not of an image${props.isVideoAllowed ? " / video" : ""} type.`);
            return false;
        }

        return true;
    };

    const initializeImageCropper = (file: File): void => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const image = event.target.result as string;
            let cropper: Cropper;

            if (image) {
                const cropperModal = document.getElementById(`template-cropper-modal-${props.uploaderId}`);
                cropperModal.style.display = 'flex';

                const cropperImage = document.getElementById(`template-cropper-img-${props.uploaderId}`) as HTMLImageElement;
                cropperImage.src = image;

                cropper = new Cropper(cropperImage, {
                    aspectRatio: props.imageCropperAspectRatio,
                    viewMode: 2
                });
            }

            const btnCancel = document.getElementById(`cancel-btn-${props.uploaderId}`);
            if (btnCancel) {
                btnCancel.addEventListener('click', function () {
                    if (cropper) {
                        try {
                            cropper.getCroppedCanvas().toDataURL('image/jpeg');
                            (document.getElementById(`add-multimedia__upload-${props.uploaderId}`) as HTMLInputElement).value = '';
                            (document.getElementById(`template-cropper-img-${props.uploaderId}`) as HTMLImageElement).src = '';
                            document.getElementById(`template-cropper-modal-${props.uploaderId}`).style.display = 'none';
                            cropper.destroy();
                        } catch (error) {
                            console.error(error);
                        }
                    }
                });
            }

            const btnCrop = document.getElementById(`crop-btn-${props.uploaderId}`);
            if (btnCrop) {
                btnCrop.addEventListener('click', function () {
                    if (cropper) {
                        try {
                            const croppedImageCanvas = cropper.getCroppedCanvas();

                            croppedImageCanvas.toBlob((blob) => {
                                const croppedImage = new File([blob], 'cropped-image.jpg', {
                                    type: 'image/jpeg'
                                })

                                processImageFile(croppedImage);

                                (document.getElementById(`add-multimedia__upload-${props.uploaderId}`) as HTMLInputElement).value = '';
                                (document.getElementById(`template-cropper-img-${props.uploaderId}`) as HTMLImageElement).src = '';
                                document.getElementById(`template-cropper-modal-${props.uploaderId}`).style.display = 'none';
                                cropper.destroy();
                            }, 'image/jpeg');
                        } catch (error) {
                            console.error(error);
                        }
                    }
                });
            }
        };

        reader.readAsDataURL(file);
    };

    const processImageFile = (file: File): void => {
        file.arrayBuffer()
            .then(async (arrayBuffer) => {
                const byteArray = new Uint8Array(arrayBuffer);
                const blob = new Blob([byteArray], { type: file.type });
                const sizeInKb = blob.size / 1024;

                if (sizeInKb > cardSettings.mediaSizeMax) {
                    console.error(`The uploaded image exceeds the maximum size. File size: ${sizeInKb}kb`);
                    setFileUploadError("The uploaded image exceeds the maximum size.");
                    return;
                }

                const formData = new FormData();
                formData.append('file', blob, file.name);
                uploadTemplateFile(formData);
            }).catch(error => {
                console.error(error);
            });
    };

    const processVideoFile = async (file: File): Promise<void> => {
        const videoElement = document.createElement("video");
        const reader = new FileReader();

        reader.onload = (event) => {
            videoElement.src = event.target.result as string;
        };

        videoElement.onloadedmetadata = () => {
            if (videoElement.duration > cardSettings.mediaDurationMax) {
                console.error(`The uploaded video exceeds the maximum duration. Video duration: ${videoElement.duration}s`);
                setFileUploadError("The uploaded video exceeds the maximum duration.");
            }
        };

        // throwing error
        //const duration = await getVideoDuration(file);
        //if (duration > cardSettings.mediaDurationMax) {
        //    console.error(`The uploaded video exceeds the maximum duration. Video duration: ${duration}s`);
        //    setFileUploadError("The uploaded video exceeds the maximum duration.");
        //}

        file.arrayBuffer()
            .then(async (arrayBuffer) => {
                const byteArray = new Uint8Array(arrayBuffer);
                const blob = new Blob([byteArray], { type: file.type });
                const sizeInKb = blob.size / 1024;

                if (sizeInKb > cardSettings.mediaSizeMax) {
                    console.error(`The uploaded video exceeds the maximum size. File size: ${sizeInKb}kb`);
                    setFileUploadError("The uploaded video exceeds the maximum size.");
                    return;
                }

                const formData = new FormData();
                formData.append('file', blob, file.name);
                uploadTemplateFile(formData);
            }).catch(error => {
                console.error(error);
            });
    };

    const uploadTemplateFile = (formData: FormData): void => {
        if (formData) {
            setIsMediaLoading(true);

            DataService.uploadGiftCardTemplate(culture, id, formData)
                .then((response) => {
                    if (response && response.url.trim()) {
                        props.handleMediaChange(response.url);
                        props.setLastUploadedTemplate(response.url);
                        setFileUploadError("");
                    }
                }).catch((error) => {
                    console.error(error);
                }).finally(() => {
                    setTimeout(() => {
                        setIsMediaLoading(false);
                    }, 300);
                });
        }
    };

    //#endregion

    return (
        <>
            <div className="card-upload fadeIn">
                {isMediaLoading ? (
                    <LoadingComponent />
                ) : (
                    <>
                        <label
                            htmlFor={`add-multimedia__upload-${props.uploaderId}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className="upload-info"
                            style={{ background: dragging ? "#f0f0f0" : "unset" }}
                        >
                            <input
                                type="file"
                                key={1}
                                multiple={false}
                                id={`add-multimedia__upload-${props.uploaderId}`}
                                name={`add-multimedia__upload-${props.uploaderId}`}
                                accept={`image/*${props.isVideoAllowed ? "|video/*" : ""}`}
                                onChange={(e) => {
                                    handleMediaUpload(e);
                                    e.target.value = '';
                                }}
                            />
                            <img src="/static/icons/upload-icon.svg" alt="Upload" />

                            {isMediaDropping ? (
                                <LoadingComponent />
                            ) : (
                                <div dangerouslySetInnerHTML={{ __html: labels.get(`GiftCard.App.V2.${props.isVideoAllowed ? "CustomiseMail" : "CustomiseCard"}.Label.Upload`) }} />
                            )}

                        </label>
                        <div
                            className="imgUpload"
                            style={{ justifyContent: 'center', maxWidth: '153px', maxHeight: '178px', border: props.lastUploadedTemplate ? "" : "1px dashed var(--cl--neutral-300)", borderRadius: '8px' }}
                        >
                            {props.isVideoAllowed && isVideoFile(props.lastUploadedTemplate) ? (
                                <video
                                    autoPlay
                                    muted
                                    style={{ maxWidth: 'inherit', maxHeight: 'inherit', cursor: 'pointer', borderRadius: "8px", border: `${props.selectedMediaUrl === props.lastUploadedTemplate ? "3px solid orange" : ""}` }}
                                    src={props.lastUploadedTemplate}
                                    onClick={() => {
                                        if (props.lastUploadedTemplate) {
                                            props.handleMediaChange(props.lastUploadedTemplate);
                                        }
                                    }}
                                    hidden={!props.lastUploadedTemplate}
                                ></video>
                            ) : (
                                <img
                                    style={{ maxWidth: 'inherit', maxHeight: 'inherit', cursor: 'pointer', borderRadius: "8px", border: `${props.selectedMediaUrl === props.lastUploadedTemplate ? "3px solid orange" : ""}` }}
                                    src={props.lastUploadedTemplate}
                                    alt="Upload img"
                                    onClick={() => {
                                        if (props.lastUploadedTemplate) {
                                            props.handleMediaChange(props.lastUploadedTemplate);
                                        }
                                    }}
                                    hidden={!props.lastUploadedTemplate}
                                />
                            )}
                        </div>
                        <div id={`template-cropper-modal-${props.uploaderId}`} className="template-cropper-modal-overlay">
                            <div className="template-cropper-container">
                                <div className="cropping-img-container">
                                    <img id={`template-cropper-img-${props.uploaderId}`} src="" alt="Crop Image" />
                                </div>
                                <div className="template-crop-popup-btn-box">
                                    <button className="cancel-btn" id={`cancel-btn-${props.uploaderId}`}>
                                        {labels.get("GiftCard.App.V2.CustomiseCard.Button.CancelUpload")}
                                    </button>
                                    <button className="crop-btn" id={`crop-btn-${props.uploaderId}`}>
                                        {labels.get("GiftCard.App.V2.CustomiseCard.Button.Crop")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {fileUploadError && (
                <i className="small-fs info react-input-error-message">
                    {fileUploadError}
                </i>
            )}
        </>
    );
};

export default MediaUploader;